import { tokens } from '../tokens';
import useIsDesktop from '../useIsDesktop';

function PhoneGlyph({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      <path d="M4 5.5C4 4.7 4.7 4 5.5 4H8l2 5-2.5 1.5a11 11 0 005 5L14 13l5 2v2.5c0 .8-.7 1.5-1.5 1.5A13.5 13.5 0 014 5.5z"
        stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}

function MessageGlyph({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      <path d="M4 5h16v11H8l-4 3V5z"
        stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

export default function CrisisModal({ onClose }) {
  const isDesktop = useIsDesktop();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(6, 4, 3, 0.92)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        animation: `gl-fade 250ms ${tokens.ease}`,
        padding: isDesktop ? 40 : 0,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isDesktop ? 560 : '100%',
          height: isDesktop ? 'auto' : '100%',
          background: tokens.ground,
          border: isDesktop ? `1px solid ${tokens.edge}` : 'none',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: `gl-fade-up 400ms ${tokens.ease}`,
          position: 'relative',
        }}
      >
        {/* Warm glow at top */}
        <div aria-hidden style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 200,
          background: `radial-gradient(ellipse 80% 100% at 50% 0%, ${tokens.accent}12 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        {/* Close button */}
        <div style={{
          position: 'absolute', top: 16, right: 16, zIndex: 3,
        }}>
          <button
            className="gl-btn-bare"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${tokens.edge}`, borderRadius: '50%',
              color: tokens.textMid,
              fontFamily: tokens.fontBody, fontSize: 18, lineHeight: 1,
            }}
          >&times;</button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: isDesktop ? '48px 44px 40px' : '48px 28px 36px',
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'column',
          justifyContent: isDesktop ? 'flex-start' : 'center',
          minHeight: isDesktop ? 'auto' : '100%',
        }}>
          {/* Heading */}
          <h1 style={{
            fontFamily: tokens.fontDisplay,
            fontSize: isDesktop ? 38 : 32,
            fontWeight: tokens.displayWeight,
            letterSpacing: tokens.displayTracking,
            color: tokens.textHi,
            margin: 0,
            lineHeight: 1.15,
          }}>
            Hey friend.
          </h1>

          {/* Body */}
          <p style={{
            fontFamily: tokens.fontBody,
            fontSize: 16, lineHeight: 1.7,
            color: tokens.textMid,
            margin: '20px 0 0',
            maxWidth: 440,
          }}>
            Whatever's going on right now, you don't have to figure it out alone. We flagged this because we'd rather check in once too many than once too few. If you're in a rough spot, these people answer the phone — and they get it.
          </p>

          {/* Resources */}
          <div style={{
            marginTop: 32,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <ResourceCard
              name="988 Suicide & Crisis Lifeline"
              line="Call or text 988. 24/7. Free. Confidential."
              actions={[
                { href: 'tel:988', label: 'Call 988', icon: 'phone', primary: true },
                { href: 'sms:988', label: 'Text 988', icon: 'message' },
              ]}
            />
            <ResourceCard
              name="Crisis Text Line"
              line="Text HOME to 741741. A trained counselor will respond."
              actions={[
                { href: 'sms:741741&body=HOME', label: 'Text HOME to 741741', icon: 'message', primary: true },
              ]}
            />
            <ResourceCard
              name="The Toolkit"
              line="More resources, industry-specific support, and coping exercises."
              actions={[
                { href: '/toolkit', label: 'Open Toolkit', internal: true, primary: true },
              ]}
            />
          </div>

          {/* Reassurance */}
          <p style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 14, lineHeight: 1.6,
            color: tokens.textLow,
            margin: '28px 0 0',
          }}>
            Your post went through. This is just a check-in — nobody's in trouble.
          </p>
        </div>
      </div>
    </div>
  );
}

function ResourceCard({ name, line, actions }) {
  return (
    <div style={{
      padding: '18px 20px',
      border: `1px solid ${tokens.edge}`,
      background: tokens.wing,
    }}>
      <div style={{
        fontFamily: tokens.fontDisplay,
        fontSize: 17, color: tokens.textHi,
        letterSpacing: tokens.displayTracking,
        lineHeight: 1.3,
      }}>{name}</div>
      <div style={{
        fontFamily: tokens.fontBody, fontSize: 13,
        color: tokens.textMid, marginTop: 6, lineHeight: 1.5,
      }}>{line}</div>
      <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {actions.map((a, i) => (
          <a
            key={i}
            href={a.href}
            target={!a.internal ? undefined : undefined}
            style={{
              textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '9px 14px',
              background: a.primary ? tokens.accent : 'transparent',
              color: a.primary ? tokens.ground : tokens.accent,
              border: a.primary ? 'none' : `1px solid ${tokens.accent}77`,
              fontFamily: tokens.fontBody, fontSize: 13, fontWeight: a.primary ? 500 : 400,
              letterSpacing: '0.02em',
            }}
          >
            {a.icon === 'phone' && <PhoneGlyph color={a.primary ? tokens.ground : tokens.accent} />}
            {a.icon === 'message' && <MessageGlyph color={a.primary ? tokens.ground : tokens.accent} />}
            {a.label}
          </a>
        ))}
      </div>
    </div>
  );
}
